import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@material-ui/core';
import { ConditionRow, LogicToggle } from './MerchandisingConditionControls';
import {
  groupConditions,
  removeCondition,
  updateCondition,
  ungroupConditions,
} from '../model/conditionExpression';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

const noop = () => {};
const BuilderRow = React.memo(
  ({ condition, onItems, onUpdate, renderExtra, ...props }) => {
    const rememberItems = useCallback(items => onItems(condition.__id, items), [
      condition.__id,
      onItems,
    ]);
    return (
      <ConditionRow
        {...props}
        condition={condition}
        onUpdate={onUpdate}
        onItems={rememberItems}
        extra={renderExtra ? renderExtra(condition, onUpdate) : null}
      />
    );
  }
);

/** Controlled expression editor used by ranking and query selection. */
export default function ConditionBuilder({
  conditions,
  schema,
  productApi,
  onChange,
  onItems = noop,
  advancedConditions = true,
  allowGroups = true,
  renderExtra = null,
  languageId = null,
}) {
  const t = useLocalizedMessage();
  const [dragged, setDragged] = useState(null);
  const [target, setTarget] = useState(null);
  const itemsCallback = useRef(onItems);
  itemsCallback.current = onItems;
  const rememberItems = useCallback(
    (id, items) => itemsCallback.current(id, items),
    []
  );
  const renderGroup = (group, depth = 0) => (
    <div
      className={depth ? 'mp-condition-group' : 'mp-condition-list'}
      key={group.__id}
    >
      {depth > 0 && (
        <div className="mp-condition-group-header">
          <strong>{t('merchandising.workbench.filterGroup')}</strong>
          <Button
            size="small"
            onClick={() => onChange(ungroupConditions(conditions, group.__id))}
          >
            {t('merchandising.workbench.ungroup')}
          </Button>
        </div>
      )}
      {group.items.map((item, index) => (
        <React.Fragment key={item.__id}>
          {index > 0 &&
            (allowGroups ? (
              <LogicToggle
                value={group.match}
                onChange={match =>
                  onChange(updateCondition(conditions, group.__id, { match }))
                }
              />
            ) : (
              <span className="mp-selection-and">
                {t('merchandising.advanced.and')}
              </span>
            ))}
          {Array.isArray(item.items) ? (
            renderGroup(item, depth + 1)
          ) : (
            <BuilderRow
              condition={item}
              languageId={languageId}
              schema={schema}
              productApi={productApi}
              advancedConditions={advancedConditions}
              draggable={allowGroups}
              dragging={dragged === item.__id}
              dropTarget={target === item.__id && dragged !== item.__id}
              onDragStart={(event, id) => {
                if (!allowGroups) return;
                event.dataTransfer.setData('text/plain', id);
                setDragged(id);
              }}
              onDragEnter={id => {
                if (allowGroups && dragged && dragged !== id && depth < 2)
                  setTarget(id);
              }}
              onDragEnd={() => {
                setDragged(null);
                setTarget(null);
              }}
              onDrop={(event, id) => {
                if (!allowGroups) return;
                event.preventDefault();
                onChange(
                  groupConditions(
                    conditions,
                    event.dataTransfer.getData('text/plain'),
                    id
                  )
                );
                setDragged(null);
                setTarget(null);
              }}
              onGroup={
                allowGroups && index > 0 && depth < 2
                  ? () =>
                      onChange(
                        groupConditions(
                          conditions,
                          item.__id,
                          group.items[index - 1].__id
                        )
                      )
                  : null
              }
              onItems={rememberItems}
              onUpdate={update =>
                onChange(updateCondition(conditions, item.__id, update))
              }
              onRemove={() => onChange(removeCondition(conditions, item.__id))}
              renderExtra={renderExtra}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
  return renderGroup(conditions);
}
